import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ComponentBaseCostData {
  id: string;
  base_cost?: number;
  total_base_cost?: number;
  category_id?: string;
  category_name?: string;
}

interface ComponentBaseCostsMap {
  supplier_line_item: Record<string, ComponentBaseCostData>;
  internal_line_item: Record<string, ComponentBaseCostData>;
  assembly: Record<string, ComponentBaseCostData>;
}

export const useComponentBaseCosts = (lineItems: any[] | undefined) => {
  return useQuery({
    queryKey: ['componentBaseCosts', lineItems?.map(li => li.id).join(',')],
    queryFn: async (): Promise<ComponentBaseCostsMap> => {
      if (!lineItems || lineItems.length === 0) {
        return {
          supplier_line_item: {},
          internal_line_item: {},
          assembly: {},
        };
      }

      // Group component IDs by type
      const supplierIds: string[] = [];
      const internalIds: string[] = [];
      const assemblyIds: string[] = [];

      lineItems.forEach(item => {
        if (item.component_type === 'supplier_line_item' && item.component_id) {
          supplierIds.push(item.component_id);
        } else if (item.component_type === 'internal_line_item' && item.component_id) {
          internalIds.push(item.component_id);
        } else if (item.component_type === 'assembly' && item.component_id) {
          assemblyIds.push(item.component_id);
        }
      });

      const result: ComponentBaseCostsMap = {
        supplier_line_item: {},
        internal_line_item: {},
        assembly: {},
      };

      // Fetch supplier line items base costs (now stored in internal_line_items)
      if (supplierIds.length > 0) {
        const { data: supplierData, error: supplierError } = await supabase
          .from('internal_line_items')
          .select('id, base_cost, category:supplier_categories!category_id(name)')
          .in('id', supplierIds);

        if (supplierError) {
          console.error('[useComponentBaseCosts] Error fetching supplier costs:', supplierError);
        } else if (supplierData) {
          supplierData.forEach((item: any) => {
            result.supplier_line_item[item.id] = {
              id: item.id,
              base_cost: item.base_cost,
              category_name: (item.category as any)?.name,
            };
          });
        }
      }

      // Fetch internal line items base costs
      if (internalIds.length > 0) {
        // Step 1: Fetch internal line items with base_cost and direct category_id
        const { data: internalData, error: internalError } = await supabase
          .from('internal_line_items')
          .select('id, base_cost, category_id')
          .in('id', internalIds);

        if (internalError) {
          console.error('[useComponentBaseCosts] Error fetching internal line items:', internalError);
        } else if (internalData) {
          // Step 2: Fetch category mappings via junction table
          const { data: mappings, error: mappingsError } = await supabase
            .from('internal_line_item_category_mappings')
            .select('line_item_id, category_id')
            .in('line_item_id', internalIds);

          if (mappingsError) {
            console.error('[useComponentBaseCosts] Error fetching category mappings:', mappingsError);
          }

          // Step 3: Get all unique category IDs (from both direct and junction table)
          const categoryIds = new Set<string>();
          internalData.forEach(item => {
            if (item.category_id) categoryIds.add(item.category_id);
          });
          mappings?.forEach(mapping => {
            if (mapping.category_id) categoryIds.add(mapping.category_id);
          });

          // Step 4: Fetch category names
          let categoryMap: Record<string, string> = {};
          if (categoryIds.size > 0) {
            const { data: categories, error: categoriesError } = await supabase
              .from('supplier_categories')
              .select('id, name')
              .in('id', Array.from(categoryIds));

            if (categoriesError) {
              console.error('[useComponentBaseCosts] Error fetching categories:', categoriesError);
            } else if (categories) {
              categories.forEach(cat => {
                categoryMap[cat.id] = cat.name;
              });
            }
          }

          // Step 5: Build result with category names (junction table takes precedence)
          internalData.forEach(item => {
            const junctionMapping = mappings?.find(m => m.line_item_id === item.id);
            const categoryId = junctionMapping?.category_id || item.category_id;
            const categoryName = categoryId ? categoryMap[categoryId] : undefined;

            result.internal_line_item[item.id] = {
              id: item.id,
              base_cost: item.base_cost,
              category_id: categoryId,
              category_name: categoryName,
            };
          });
        }
      }

      // Fetch assemblies base costs (calculated recursively from components)
      if (assemblyIds.length > 0) {
        // Step 1: Fetch all assembly components
        const { data: assemblyComponents, error: assemblyError } = await supabase
          .from('assembly_components')
          .select('assembly_id, component_id, component_type, quantity')
          .in('assembly_id', assemblyIds);

        if (assemblyError) {
          console.error('[useComponentBaseCosts] Error fetching assembly components:', assemblyError);
        }

        // Step 2: Group components by assembly
        const assemblyComponentMap = new Map<string, any[]>();
        assemblyComponents?.forEach(comp => {
          if (!assemblyComponentMap.has(comp.assembly_id)) {
            assemblyComponentMap.set(comp.assembly_id, []);
          }
          assemblyComponentMap.get(comp.assembly_id)!.push(comp);
        });

        // Step 3: Collect all component IDs by type
        const neededSupplierIds: string[] = [];
        const neededInternalIds: string[] = [];
        assemblyComponents?.forEach(comp => {
          if (comp.component_type === 'supplier_line_item') {
            neededSupplierIds.push(comp.component_id);
          } else if (comp.component_type === 'internal_line_item') {
            neededInternalIds.push(comp.component_id);
          }
        });

        // Step 4: Fetch component costs (only if not already fetched)
        const additionalSupplierIds = neededSupplierIds.filter(id => !supplierIds.includes(id));
        const additionalInternalIds = neededInternalIds.filter(id => !internalIds.includes(id));

        if (additionalSupplierIds.length > 0) {
          const { data: additionalSupplierData, error: additionalSupplierError } = await supabase
            .from('internal_line_items')
            .select('id, base_cost')
            .in('id', additionalSupplierIds);

          if (additionalSupplierError) {
            console.error('[useComponentBaseCosts] Error fetching additional supplier costs:', additionalSupplierError);
          } else if (additionalSupplierData) {
            additionalSupplierData.forEach(item => {
              if (!result.supplier_line_item[item.id]) {
                result.supplier_line_item[item.id] = {
                  id: item.id,
                  base_cost: item.base_cost,
                };
              }
            });
          }
        }

        if (additionalInternalIds.length > 0) {
          const { data: additionalInternalData, error: additionalInternalError } = await supabase
            .from('internal_line_items')
            .select('id, base_cost')
            .in('id', additionalInternalIds);

          if (additionalInternalError) {
            console.error('[useComponentBaseCosts] Error fetching additional internal costs:', additionalInternalError);
          } else if (additionalInternalData) {
            additionalInternalData.forEach(item => {
              if (!result.internal_line_item[item.id]) {
                result.internal_line_item[item.id] = {
                  id: item.id,
                  base_cost: item.base_cost,
                };
              }
            });
          }
        }

        // Step 5: Calculate total base cost for each assembly
        assemblyIds.forEach(assemblyId => {
          const components = assemblyComponentMap.get(assemblyId) || [];
          let totalBaseCost = 0;

          components.forEach(comp => {
            let componentBaseCost = 0;
            if (comp.component_type === 'supplier_line_item') {
              componentBaseCost = result.supplier_line_item[comp.component_id]?.base_cost || 0;
            } else if (comp.component_type === 'internal_line_item') {
              componentBaseCost = result.internal_line_item[comp.component_id]?.base_cost || 0;
            }
            totalBaseCost += componentBaseCost * comp.quantity;
          });

          result.assembly[assemblyId] = {
            id: assemblyId,
            total_base_cost: totalBaseCost,
            category_name: 'Assembly',
          };
        });
      }

      return result;
    },
    enabled: !!lineItems && lineItems.length > 0,
    retry: false,
    placeholderData: (previousData) => previousData, // Keep previous data during refetch to prevent blinking
  });
};
